import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Radio, User, Lock, Mail, Bell, Shield, History,
  Trash2, Download, LogOut, MessageCircle, FileText, Eye, Crown, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const headers = { Authorization: `Bearer ${token}` };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'privacy', label: 'Privacy & Data', icon: Shield },
    { id: 'sessions', label: 'Sessions', icon: History },
    { id: 'legal', label: 'Legal', icon: FileText },
    { id: 'support', label: 'Support', icon: MessageCircle },
    { id: 'danger', label: 'Danger Zone', icon: Trash2 },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#08080A' }}>
      <nav className="sticky top-0 z-50 border-b px-4 sm:px-8 py-3" style={{ borderColor: 'rgba(255,255,255,0.05)', backgroundColor: 'rgba(8,8,10,0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <Radio className="w-5 h-5 text-purple-500" />
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>Opposite Direction</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />Back
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="text-2xl font-light tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }} data-testid="settings-title">Account Settings</h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="md:w-48 flex-shrink-0">
            <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-purple-500/10 text-purple-400' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`}
                  data-testid={`settings-tab-${t.id}`}>
                  <t.icon className="w-3.5 h-3.5" />{t.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'profile' && <ProfileSection headers={headers} user={user} refreshUser={refreshUser} />}
            {activeTab === 'security' && <SecuritySection headers={headers} user={user} />}
            {activeTab === 'notifications' && <NotificationsSection headers={headers} />}
            {activeTab === 'subscription' && <SubscriptionSection headers={headers} user={user} navigate={navigate} />}
            {activeTab === 'privacy' && <PrivacySection headers={headers} />}
            {activeTab === 'sessions' && <SessionsSection headers={headers} />}
            {activeTab === 'legal' && <LegalSection headers={headers} />}
            {activeTab === 'support' && <SupportSection headers={headers} />}
            {activeTab === 'danger' && <DangerSection headers={headers} user={user} logout={logout} navigate={navigate} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const Card = ({ title, children, className = '' }) => (
  <div className={`rounded-xl border p-5 mb-4 ${className}`} style={{ backgroundColor: '#141417', borderColor: 'rgba(255,255,255,0.06)' }}>
    {title && <h3 className="text-sm font-medium text-white mb-4">{title}</h3>}
    {children}
  </div>
);

// ============ PROFILE ============
const ProfileSection = ({ headers, user, refreshUser }) => {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendToken, setResendToken] = useState('');

  const saveName = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/account/profile`, { name }, { headers });
      toast.success('Name updated');
      refreshUser?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const saveEmail = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/account/email`, { email, password: emailPassword }, { headers });
      toast.success('Email updated');
      setEmailPassword('');
      refreshUser?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, {}, { headers });
      toast.success(res.data.message);
      if (res.data.verify_token) {
        setResendToken(res.data.verify_token);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setResending(false);
  };

  return (
    <div>
      {/* Email Verification Status */}
      {user && !user.email_verified && (
        <Card title="Email Verification">
          <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: 'rgba(234, 179, 8, 0.05)', border: '1px solid rgba(234, 179, 8, 0.15)' }}>
            <p className="text-xs text-yellow-400 mb-2">Your email is not verified yet. Please check your inbox or resend the verification email.</p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={resendVerification} disabled={resending} className="bg-yellow-600 hover:bg-yellow-500 text-white text-xs" data-testid="resend-verification-btn">
                {resending ? 'Sending...' : 'Resend Verification'}
              </Button>
            </div>
            {resendToken && (
              <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(109, 40, 217, 0.1)' }}>
                <p className="text-xs text-purple-300 mb-1">SMTP not configured. Direct link:</p>
                <Link to={`/verify-email?token=${resendToken}`} className="text-xs text-purple-400 underline" data-testid="verify-link">Verify Email Now</Link>
              </div>
            )}
          </div>
        </Card>
      )}
      {user?.email_verified && (
        <Card>
          <div className="flex items-center gap-2 text-emerald-400 text-xs">
            <Mail className="w-3.5 h-3.5" />
            Email verified
          </div>
        </Card>
      )}
      <Card title="Display Name">
        <div className="flex gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} className="bg-[#1C1C21] border-white/10 text-white text-sm flex-1" data-testid="settings-name-input" />
          <Button size="sm" onClick={saveName} disabled={saving} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="save-name-btn">Save</Button>
        </div>
      </Card>
      <Card title="Email Address">
        <div className="space-y-2">
          <Input value={email} onChange={e => setEmail(e.target.value)} className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="settings-email-input" />
          <Input type="password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} placeholder="Enter current password to confirm" className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="email-password-confirm" />
          <Button size="sm" onClick={saveEmail} disabled={saving || !emailPassword} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="save-email-btn">Update Email</Button>
        </div>
      </Card>
    </div>
  );
};

// ============ SECURITY ============
const SecuritySection = ({ headers, user }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/account/password`, { current_password: currentPw, new_password: newPw }, { headers });
      toast.success('Password changed');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div>
      <Card title="Change Password">
        <div className="space-y-3">
          <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Current password" className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="current-password" />
          <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (min 8, uppercase, lowercase, number)" className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="new-password" />
          <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm new password" className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="confirm-password" />
          <Button size="sm" onClick={changePassword} disabled={saving || !currentPw || !newPw} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="change-password-btn">Change Password</Button>
        </div>
      </Card>
      <Card title="Forgot Password">
        <p className="text-xs text-gray-500 mb-3">Need to reset your password via email? Use our dedicated reset page.</p>
        <Link to="/reset-password">
          <Button size="sm" variant="outline" className="border-white/10 text-purple-400 text-xs" data-testid="go-reset-page-btn">Go to Password Reset</Button>
        </Link>
      </Card>
    </div>
  );
};

// ============ NOTIFICATIONS ============
const NotificationsSection = ({ headers }) => {
  const [prefs, setPrefs] = useState({ email_updates: true, new_features: true, weekly_digest: true, marketing: false });

  useEffect(() => {
    axios.get(`${API}/account/profile`, { headers }).then(r => {
      const p = r.data.profile?.notification_preferences;
      if (p) setPrefs(p);
    }).catch((e) => console.error('Failed to load notification prefs:', e.message));
  }, [headers]);

  const save = async () => {
    try {
      await axios.put(`${API}/account/notifications`, prefs, { headers });
      toast.success('Preferences saved');
    } catch (e) { console.error(e); toast.error('Failed'); }
  };

  const Toggle = ({ label, field }) => (
    <label className="flex items-center justify-between py-2">
      <span className="text-xs text-gray-300">{label}</span>
      <div onClick={() => setPrefs(p => ({ ...p, [field]: !p[field] }))}
        className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${prefs[field] ? 'bg-purple-600' : 'bg-gray-700'}`}
        data-testid={`notif-toggle-${field}`}>
        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${prefs[field] ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
    </label>
  );

  return (
    <Card title="Notification Preferences">
      <div className="divide-y divide-white/5">
        <Toggle label="Email Updates" field="email_updates" />
        <Toggle label="New Feature Announcements" field="new_features" />
        <Toggle label="Weekly Digest" field="weekly_digest" />
        <Toggle label="Marketing Emails" field="marketing" />
      </div>
      <Button size="sm" onClick={save} className="mt-4 bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="save-notifications-btn">Save Preferences</Button>
    </Card>
  );
};

// ============ SUBSCRIPTION ============
const SubscriptionSection = ({ headers, user, navigate }) => {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    axios.get(`${API}/account/subscription`, { headers }).then(r => setSub(r.data)).catch((e) => console.error('Failed to load subscription:', e.message));
  }, [headers]);

  const cancel = async () => {
    if (!window.confirm('Cancel your premium subscription?')) return;
    try {
      await axios.post(`${API}/account/cancel-subscription`, {}, { headers });
      toast.success('Subscription cancelled');
      setSub(s => ({ ...s, is_premium: false }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card title="Subscription Status">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sub?.is_premium ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}>
            {sub?.is_premium ? 'Premium' : 'Free'}
          </span>
          {sub?.plan && sub.plan !== 'free' && <span className="text-xs text-gray-500">Plan: {sub.plan}</span>}
        </div>
        {sub?.is_premium ? (
          <Button size="sm" variant="outline" onClick={cancel} className="border-red-500/30 text-red-400 text-xs" data-testid="cancel-subscription-btn">Cancel Subscription</Button>
        ) : (
          <Button size="sm" onClick={() => navigate('/subscription')} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="upgrade-btn">Upgrade to Premium</Button>
        )}
      </div>
    </Card>
  );
};

// ============ PRIVACY & DATA ============
const PrivacySection = ({ headers }) => {
  const [quotePublic, setQuotePublic] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/profile`, { headers }).then(r => {
      setQuotePublic(r.data.profile?.quote_sharing_public !== false);
    }).catch((e) => console.error('Failed to load privacy settings:', e.message));
  }, [headers]);

  const savePrivacy = async () => {
    try {
      await axios.put(`${API}/account/quote-privacy`, { quote_sharing_public: quotePublic }, { headers });
      toast.success('Privacy settings saved');
    } catch (e) { console.error(e); toast.error('Failed'); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/account/export-data`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'opposite-direction-data-export.json'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (e) { console.error(e); toast.error('Export failed'); }
    setExporting(false);
  };

  return (
    <div>
      <Card title="Quote Sharing Privacy">
        <label className="flex items-center justify-between">
          <span className="text-xs text-gray-300">Allow my quotes to appear in public feeds</span>
          <div onClick={() => setQuotePublic(p => !p)}
            className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${quotePublic ? 'bg-purple-600' : 'bg-gray-700'}`}
            data-testid="quote-privacy-toggle">
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${quotePublic ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
        </label>
        <Button size="sm" onClick={savePrivacy} className="mt-3 bg-purple-600 hover:bg-purple-500 text-white text-xs">Save</Button>
      </Card>
      <Card title="Export My Data">
        <p className="text-xs text-gray-500 mb-3">Download all your account data, podcasts, and activity.</p>
        <Button size="sm" onClick={exportData} disabled={exporting} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="export-data-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" />{exporting ? 'Exporting...' : 'Export Data'}
        </Button>
      </Card>
    </div>
  );
};

// ============ SESSIONS ============
const SessionsSection = ({ headers }) => {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/account/sessions`, { headers }).then(r => setSessions(r.data.sessions || [])).catch((e) => console.error('Failed to load sessions:', e.message));
  }, [headers]);

  const logoutAll = async () => {
    try {
      const res = await axios.post(`${API}/account/logout-all`, {}, { headers });
      toast.success(`Logged out from ${res.data.sessions_cleared} devices`);
      setSessions([]);
    } catch (e) { console.error(e); toast.error('Failed'); }
  };

  return (
    <Card title="Active Sessions">
      {sessions.length === 0 ? (
        <p className="text-xs text-gray-500">No active sessions (JWT-based auth).</p>
      ) : (
        <div className="space-y-2 mb-3">
          {sessions.map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-xs text-gray-300">{s.created_at ? new Date(s.created_at).toLocaleString() : 'Active'}</span>
            </div>
          ))}
        </div>
      )}
      <Button size="sm" variant="outline" onClick={logoutAll} className="border-red-500/30 text-red-400 text-xs" data-testid="logout-all-btn">
        <LogOut className="w-3.5 h-3.5 mr-1.5" />Logout from All Devices
      </Button>
    </Card>
  );
};

// ============ LEGAL ============
const LegalSection = ({ headers }) => {
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/account/legal-acceptance`, { headers }).then(r => setLegal(r.data)).catch((e) => console.error('Failed to load legal acceptance:', e.message));
  }, [headers]);

  const reaccept = async () => {
    try {
      await axios.post(`${API}/account/reaccept-terms`, { policy_version: legal?.current_policy_version || '1.0' }, { headers });
      toast.success('Terms re-accepted');
      setLegal(l => ({ ...l, terms_accepted_at: new Date().toISOString(), terms_policy_version: l?.current_policy_version }));
    } catch (e) { console.error(e); toast.error('Failed'); }
  };

  return (
    <Card title="Legal Policy Acceptance">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-300">Terms Accepted</span>
          <span className="text-xs text-gray-500">{legal?.terms_accepted_at ? new Date(legal.terms_accepted_at).toLocaleDateString() : 'Not yet'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-300">Policy Version</span>
          <span className="text-xs text-gray-500">{legal?.terms_policy_version || 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-300">Current Version</span>
          <span className="text-xs text-purple-400">{legal?.current_policy_version || '1.0'}</span>
        </div>
        {legal && legal.terms_policy_version !== legal.current_policy_version && (
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <p className="text-xs text-yellow-400 mb-2">Policy has been updated. Please review and re-accept.</p>
            <div className="flex gap-2">
              <Link to="/terms" className="text-xs text-purple-400 underline">Review Terms</Link>
              <Link to="/privacy" className="text-xs text-purple-400 underline">Review Privacy</Link>
            </div>
            <Button size="sm" onClick={reaccept} className="mt-2 bg-yellow-600 hover:bg-yellow-500 text-white text-xs" data-testid="reaccept-btn">Accept Updated Policies</Button>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <Link to="/terms" className="text-xs text-purple-400 hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="text-xs text-purple-400 hover:underline">Privacy Policy</Link>
          <Link to="/cookies" className="text-xs text-purple-400 hover:underline">Cookie Policy</Link>
          <Link to="/refund-policy" className="text-xs text-purple-400 hover:underline">Refund Policy</Link>
        </div>
      </div>
    </Card>
  );
};

// ============ SUPPORT ============
const SupportSection = ({ headers }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [tickets, setTickets] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/support-tickets`, { headers }).then(r => setTickets(r.data.tickets || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/account/support`, { subject, message }, { headers });
      toast.success('Support ticket submitted');
      setSubject(''); setMessage('');
      const res = await axios.get(`${API}/account/support-tickets`, { headers });
      setTickets(res.data.tickets || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  return (
    <div>
      <Card title="Contact Support">
        <div className="space-y-3">
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="bg-[#1C1C21] border-white/10 text-white text-sm" data-testid="support-subject" />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue..." className="w-full bg-[#1C1C21] border border-white/10 text-white text-sm rounded-lg p-3 h-24 resize-none" data-testid="support-message" />
          <Button size="sm" onClick={submit} disabled={sending || !message.trim()} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="submit-ticket-btn">
            {sending ? 'Sending...' : 'Submit Ticket'}
          </Button>
        </div>
      </Card>
      {tickets.length > 0 && (
        <Card title="Your Tickets">
          <div className="space-y-2">
            {tickets.map(t => (
              <div key={t.ticket_id} className="py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white font-medium">{t.subject}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.status === 'open' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{t.status}</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5 truncate">{t.message}</p>
                {t.admin_response && <p className="text-[10px] text-purple-400 mt-1">Response: {t.admin_response}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ============ DANGER ZONE ============
const DangerSection = ({ headers, user, logout, navigate }) => {
  const [deletePw, setDeletePw] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const requestDeletion = async () => {
    if (confirmText !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    try {
      await axios.post(`${API}/account/delete-request`, { password: deletePw }, { headers });
      toast.success('Account scheduled for deletion');
      await logout();
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card title="Danger Zone" className="border-red-500/20">
      <div className="space-y-4">
        <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
          <p className="text-xs text-red-400 font-medium mb-1">Delete Account</p>
          <p className="text-[10px] text-gray-500 mb-3">This will permanently delete your account and all data after 14 days. This action cannot be undone.</p>
          <Input type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)} placeholder="Enter your password" className="bg-[#1C1C21] border-white/10 text-white text-sm mb-2" data-testid="delete-password" />
          <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type DELETE to confirm" className="bg-[#1C1C21] border-white/10 text-white text-sm mb-2" data-testid="delete-confirm" />
          <Button size="sm" onClick={requestDeletion} disabled={confirmText !== 'DELETE'} className="bg-red-600 hover:bg-red-500 text-white text-xs" data-testid="delete-account-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete My Account
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default SettingsPage;
